import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareCaretLeft,
  faSquareCaretRight,
} from "@fortawesome/free-solid-svg-icons";
import "./HowItWorksModal.css";

const LEVELS = [
  {
    label: "Level 1",
    steps: [
      {
        heading: "Plan & journal your day.",
        body: "Each day, mindfully plan and journal in your NoWastedDays.",
        media: { label: "figure c: the daily journal" },
      },
      {
        heading: "Unlock your Explore page after 7 days.",
        body: "After 7 journal entries, you unlock your Explore page — a personal insight space built entirely for you.",
        media: { label: "figure e: the insights hub" },
      },
      {
        heading: "Receive safe, smart guidance and coaching.",
        body: "Unearth unique insights through tailored guidance and coaching to help you understand yourself more and have happier, healthier days.",
      },
      {
        heading: "You've started a beautiful feedback loop.",
        body: "Everything you input into your NoWastedDays becomes an interesting data point in your Explore page which can inspire new understanding, and become a new input down the line. This feedback loop is a safe and happy to grow and learn.",
      },
    ],
  },
  {
    label: "Level 2",
    steps: [
      {
        heading: "Mind (and learn to love) the gap",
        body: "One powerful signal we are all missing most of the time that every journal day you log contributes to is the gap between what you planned & predicted you would do and what you actually did & what made the day unique",
      },
      {
        heading: "What the gap means.",
        body: "Sometimes what that gap means is clear right on the surface. Other times, it's beautifully nuanced, messy and unexpected. Either way, NoWastedDays is designed to help you search safely and smartly for answers and ideas.",
      },
      {
        heading: "Help and guidance from 'The Art of Looking'",
        body: "Our unique 'Art of Looking' learning journey guides you to look at your own data smartly and safely in order to continuous build real self-awareness without the overwhelm and anxiety.",
        media: { label: "the art of looking" },
      },
      {
        heading: "Additional AI & Human Support",
        body: "Our AI highlights questions you may not have thought to ask and draws attention to days that are easy to overlook without ever using your data to train its model. Human support is available via the support button in the footer.",
      },
    ],
  },
  {
    label: "Level 3",
    steps: [
      {
        heading: "Our beautiful but bounded brains",
        body: "Our brains are wired to survive. They are trained to construct stories, selectively remember and narrow focus to respond to stressors and maximum our chance of survival. This is the opposite of what we need to do in order to develop self-awareness.",
      },
      {
        heading: "Truth lives in the details",
        body: "Developing self-awareness requires exploring the spaces that your brain prefers to forget — sitting with the uncertainty between question and answer for a little longer, be open to new information and generally ask more questions.",
      },
      {
        heading: "Self-awareness is glorious",
        body: "It is one of the most important mental skills for a healthy, happy, resilient, peaceful and joyful life. Knowing this, it is clear technology should be designed to help.",
      },
      {
        heading: "NWD fills the gap",
        body: "NoWastedDays keeps something closer to the truth of your life within arm's reach - in a safe space, encrypted to only help you - whenever you have the time and inclination to explore it.",
      },
    ],
  },
];

const DATA_POINTS = [
  {
    heading: "Exists for you.",
    body: "The only reason your data exists is to help you learn more about yourself. It is not designed to serve any other purpose apart from that.",
  },
  {
    heading: "Fully encrypted.",
    body: "Advanced encryption means your journal cannot be viewed by anyone, including us.",
  },
  {
    heading: "Never sold.",
    body: "Your data will never be sold or shared with third parties for commercial reasons — full stop, no exceptions.",
  },
  {
    heading: "Delete anytime.",
    body: "You can delete everything, immediately and at no cost, whenever you choose.",
  },
];

/**
 * HowItWorksModal
 *
 * Props:
 *   onClose  — called when the user dismisses the modal
 *   mode     — 'onboarding' shows "Let's start journaling →" CTA
 *              'reference'  shows "Close" CTA
 */
export default function HowItWorksModal({ onClose, mode = "reference" }) {
  const [activeTab, setActiveTab] = useState("how");
  const [activeLevel, setActiveLevel] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [activeDataStep, setActiveDataStep] = useState(0);
  const [dataDir, setDataDir] = useState(1);
  const [maxLevel1Step, setMaxLevel1Step] = useState(0);

  const level1StepCount = LEVELS[0].steps.length;
  const ctaReady =
    mode !== "onboarding" || maxLevel1Step >= level1StepCount - 1;
  const ctaLabel = mode === "onboarding" ? "Let's get started" : "Close";
  const currentLevel = LEVELS[activeLevel];
  const totalSteps = currentLevel.steps.length;
  const currentStep = currentLevel.steps[activeStep];
  const totalDataSteps = DATA_POINTS.length;
  const currentDataPoint = DATA_POINTS[activeDataStep];

  function goToLevel(i) {
    setActiveLevel(i);
    setActiveStep(0);
    setDir(1);
  }

  function goToStep(i) {
    setDir(i > activeStep ? 1 : -1);
    setActiveStep(i);
    if (activeLevel === 0 && i > maxLevel1Step) setMaxLevel1Step(i);
  }

  function handleNext() {
    if (activeStep < totalSteps - 1) goToStep(activeStep + 1);
  }

  function handlePrev() {
    if (activeStep > 0) goToStep(activeStep - 1);
  }

  function goToDataStep(i) {
    setDataDir(i > activeDataStep ? 1 : -1);
    setActiveDataStep(i);
  }

  function handleDataNext() {
    if (activeDataStep < totalDataSteps - 1) goToDataStep(activeDataStep + 1);
  }

  function handleDataPrev() {
    if (activeDataStep > 0) goToDataStep(activeDataStep - 1);
  }

  return (
    <div className="hiw-overlay" onClick={onClose}>
      <div className="hiw-card" onClick={(e) => e.stopPropagation()}>
        {/* Header — tabs act as title */}
        <div className="hiw-header">
          <div className="hiw-tabs">
            <button
              className={`hiw-tab${
                activeTab === "how" ? " hiw-tab--active" : ""
              }`}
              onClick={() => setActiveTab("how")}
            >
              How NoWastedDays works
            </button>
            <button
              className={`hiw-tab${
                activeTab === "data" ? " hiw-tab--active" : ""
              }`}
              onClick={() => setActiveTab("data")}
            >
              Your data
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="hiw-body">
          {activeTab === "how" ? (
            <>
              <div
                className={`hiw-step-card hiw-step-card--${
                  dir > 0 ? "forward" : "back"
                }`}
                key={`${activeLevel}-${activeStep}`}
              >
                {currentStep.media && (
                  <div className="hiw-media-placeholder">
                    <span className="hiw-media-label">
                      {currentStep.media.label}
                    </span>
                  </div>
                )}
                <strong className="hiw-step-heading">
                  {currentStep.heading}
                </strong>
                <p className="hiw-step-body">{currentStep.body}</p>
              </div>

              <div className="hiw-step-nav">
                <button
                  className="hiw-step-arrow"
                  onClick={handlePrev}
                  disabled={activeStep === 0}
                  aria-label="Previous step"
                >
                  <FontAwesomeIcon
                    icon={faSquareCaretLeft}
                    className="hiw-nav-icon"
                  />
                </button>
                <div className="hiw-step-dots">
                  {currentLevel.steps.map((_, i) => (
                    <button
                      key={i}
                      className={`hiw-dot${
                        i === activeStep ? " hiw-dot--active" : ""
                      }`}
                      onClick={() => goToStep(i)}
                      aria-label={`Go to step ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  className="hiw-step-arrow"
                  onClick={handleNext}
                  disabled={activeStep === totalSteps - 1}
                  aria-label="Next step"
                >
                  <FontAwesomeIcon
                    icon={faSquareCaretRight}
                    className="hiw-nav-icon"
                  />
                </button>
              </div>

              <p className="hiw-step-counter">
                {activeStep + 1} of {totalSteps}
              </p>
            </>
          ) : (
            <>
              <div
                className={`hiw-step-card hiw-step-card--${
                  dataDir > 0 ? "forward" : "back"
                }`}
                key={`data-${activeDataStep}`}
              >
                <strong className="hiw-step-heading">
                  {currentDataPoint.heading}
                </strong>
                <p className="hiw-step-body">{currentDataPoint.body}</p>
              </div>

              <div className="hiw-step-nav">
                <button
                  className="hiw-step-arrow"
                  onClick={handleDataPrev}
                  disabled={activeDataStep === 0}
                  aria-label="Previous"
                >
                  <FontAwesomeIcon
                    icon={faSquareCaretLeft}
                    className="hiw-nav-icon"
                  />
                </button>
                <div className="hiw-step-dots">
                  {DATA_POINTS.map((_, i) => (
                    <button
                      key={i}
                      className={`hiw-dot${
                        i === activeDataStep ? " hiw-dot--active" : ""
                      }`}
                      onClick={() => goToDataStep(i)}
                      aria-label={`Go to point ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  className="hiw-step-arrow"
                  onClick={handleDataNext}
                  disabled={activeDataStep === totalDataSteps - 1}
                  aria-label="Next"
                >
                  <FontAwesomeIcon
                    icon={faSquareCaretRight}
                    className="hiw-nav-icon"
                  />
                </button>
              </div>

              <p className="hiw-step-counter">
                {activeDataStep + 1} of {totalDataSteps}
              </p>
            </>
          )}
        </div>

        {/* Footer — level buttons left, help + CTA right */}
        <div className="hiw-footer">
          <div className="hiw-footer-left">
            {activeTab === "how" && (
              <div className="hiw-levels">
                {LEVELS.map((level, i) => (
                  <button
                    key={level.label}
                    className={`hiw-level-btn${
                      activeLevel === i ? " hiw-level-btn--active" : ""
                    }`}
                    onClick={() => goToLevel(i)}
                  >
                    {level.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="hiw-footer-right">
            <a
              href="/my-account/faqs"
              className="hiw-help-btn"
              onClick={onClose}
            >
              I need more help
            </a>
            <button
              className="hiw-cta-btn"
              onClick={onClose}
              disabled={!ctaReady}
              title={
                !ctaReady ? "Read through Level 1 to get started" : undefined
              }
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
